// src/pages/Wallet/modals/TokenChart.js
import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  parseOHLCVData, 
  filterDataByTimeframe, 
  getChartColor, 
  formatAxisDate,
  buildOHLCVApiUrl,
  calculatePriceChange
} from '../../../utils/ohlcvUtils';
import secureFetch from '../../../utils/secureFetch'; 
import PriceChange from '../components/PriceChange';
import './TokenChart.css';
import '../components/PriceChange.css';

const TokenChart = ({ tokenAddress, network = 'bsc' }) => {
  const [pool, setPool] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch token data to find the first pool
  useEffect(() => {
    const fetchTokenData = async () => {
      if (!tokenAddress || tokenAddress.toLowerCase() === 'native') {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Use secureFetch instead of fetch
        const response = await secureFetch(`https://api.geckoterminal.com/api/v2/networks/${network}/tokens/${tokenAddress}`);
        const data = await response.json();
        
        if (data.data?.relationships?.top_pools?.data?.length > 0) {
          const poolId = data.data.relationships.top_pools.data[0].id;
          const poolAddress = poolId.split('_')[1];
          setPool(poolAddress);
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error fetching token data:", err);
        setIsLoading(false);
      }
    };
    
    fetchTokenData();
  }, [tokenAddress, network]);

  // Fetch OHLCV data based on the selected timeframe
  useEffect(() => {
    const fetchChartData = async () => {
      if (!pool) return;
      
      try {
        setIsLoading(true);
        
        // Build the API URL using the utility function
        const url = buildOHLCVApiUrl(network, pool, selectedTimeframe);
        
        // Use secureFetch instead of fetch
        const response = await secureFetch(url);
        const data = await response.json();
        
        if (data.data?.attributes?.ohlcv_list) {
          // Parse the OHLCV data
          const parsedData = parseOHLCVData(data.data.attributes.ohlcv_list);
          
          // Sort by timestamp (oldest to newest)
          parsedData.sort((a, b) => a.timestamp - b.timestamp);
          
          // Filter based on timeframe
          const filteredData = filterDataByTimeframe(parsedData, selectedTimeframe);
          
          // Format data for charting (we only need timestamp and close price)
          const chartReadyData = filteredData.map(item => ({
            timestamp: item.timestamp,
            price: item.close
          }));
          
          setChartData(chartReadyData);
        } else {
          setChartData([]);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching chart data:", err);
        setError("Failed to load chart data");
        setIsLoading(false);
      }
    };
    
    if (pool) {
      fetchChartData();
    }
  }, [pool, selectedTimeframe, network]);

  // Format X-axis ticks based on the selected timeframe
  const formatXAxis = (timestamp) => {
    return formatAxisDate(timestamp, selectedTimeframe);
  };
  
  // Get chart color based on price trend using utility function
  const getLineColor = () => {
    // Create data structure compatible with the utility function
    const ohlcvData = chartData.map(item => ({
      close: item.price,
      timestamp: item.timestamp
    }));
    
    return getChartColor(ohlcvData);
  };

  // Calculate the price change percentage for the current data
  const getPriceChangePercentage = () => {
    if (chartData.length < 2) return null;
    
    const firstPrice = chartData[0].price;
    const lastPrice = chartData[chartData.length - 1].price;
    
    return ((lastPrice - firstPrice) / firstPrice) * 100;
  };
  
  return (
    <div className="token-chart-wrapper">
      <div className="chart-header">
        <div className="timeframe-tabs">
          <button 
            className={`timeframe-button ${selectedTimeframe === '1h' ? 'active' : ''}`} 
            onClick={() => setSelectedTimeframe('1h')}
          >
            1H
          </button>
          <button 
            className={`timeframe-button ${selectedTimeframe === '24h' ? 'active' : ''}`} 
            onClick={() => setSelectedTimeframe('24h')}
          >
            24H
          </button>
          <button 
            className={`timeframe-button ${selectedTimeframe === '1w' ? 'active' : ''}`} 
            onClick={() => setSelectedTimeframe('1w')}
          >
            1W
          </button>
          <button 
            className={`timeframe-button ${selectedTimeframe === '1m' ? 'active' : ''}`} 
            onClick={() => setSelectedTimeframe('1m')}
          >
            1M
          </button>
        </div>
        
        {/* Show price change if data is available */}
        {chartData.length > 0 && (
          <div className="price-change-wrapper">
            <PriceChange data={chartData} />
          </div>
        )}
      </div>
      
      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading chart data...</div>
        ) : error ? (
          <div className="chart-error">{error}</div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={formatXAxis}
                stroke="var(--text-tertiary)"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                domain={['auto', 'auto']} 
                tickFormatter={(value) => `${value.toFixed(6)}`}
                width={80}
                stroke="var(--text-tertiary)"
                tick={{ fontSize: 12 }}
              />
              <Tooltip 
                labelFormatter={(timestamp) => {
                  const date = new Date(timestamp);
                  return date.toLocaleString();
                }}
                formatter={(value) => [`${parseFloat(value).toFixed(6)}`, 'Price']}
                contentStyle={{ 
                  backgroundColor: 'var(--surface-bg)',
                  borderColor: 'var(--border-color)',
                  borderRadius: 'var(--border-radius-sm)'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="price" 
                stroke={getLineColor()} 
                dot={false} 
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="no-chart-data">
            {tokenAddress && tokenAddress.toLowerCase() !== 'native' ? 
              "No chart data available for this token" : 
              "Charts not available for native tokens"}
          </div>
        )}
      </div>
    </div>
  );
};

export default TokenChart;